import { useState, type FormEvent } from 'react';
import { CornerUpLeft } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import {
  useRenvoyerConvention,
  useSoumettreConvention,
  useValiderConvention,
  type VueConvention,
} from '../../lib/useConventions';

const CLASSE_BOUTON =
  'inline-flex min-h-[36px] items-center justify-center rounded-lg border px-3 py-1.5 text-[11px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[40px]';

const MOTIF_MIN = 5;

function jour(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/**
 * Circuit de validation d'une convention de formation (TF-430), sous sa ligne
 * dans le dossier : le dernier renvoi de la direction et son motif, puis le
 * geste ouvert au lecteur — soumettre pour l'administratif, valider ou
 * renvoyer pour la direction. L'API dit ce qui est permis ; l'écran n'affiche
 * que les gestes sans refus.
 */
export function ValidationConvention({
  documentId,
  apprenantId,
  convention,
  peutSoumettre,
}: {
  documentId: string;
  apprenantId: string;
  convention: VueConvention;
  /** Qui tient les documents du dossier (`adm-documents`) soumet. */
  peutSoumettre: boolean;
}): JSX.Element | null {
  const toast = useToast();
  const soumettre = useSoumettreConvention();
  const valider = useValiderConvention();
  const [renvoiOuvert, setRenvoiOuvert] = useState(false);

  const direction = convention.refusValidation === null || convention.refusRenvoi === null;
  const soumissionOuverte = peutSoumettre && convention.refusSoumission === null && convention.refusValidation !== null;
  const renvoiVisible =
    convention.renvoi !== null && (convention.etat === 'BROUILLON' || convention.etat === 'A_VALIDER');
  const enCours = soumettre.isPending || valider.isPending;
  const erreur = soumettre.error ?? valider.error;

  if (!renvoiVisible && !soumissionOuverte && !direction && convention.etat !== 'A_VALIDER') return null;

  const confirmer = (resultat: { message: string }): void => toast(resultat.message);

  return (
    <div className="mt-2 space-y-2">
      {renvoiVisible && convention.renvoi && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
          <span className="font-semibold">
            Renvoyée le {jour(convention.renvoi.le)}
            {convention.renvoi.par ? ` par ${convention.renvoi.par}` : ''}
          </span>
          <span className="whitespace-pre-line break-words"> · « {convention.renvoi.motif} »</span>
        </div>
      )}

      {convention.etat === 'A_VALIDER' && !direction && (
        <p className="text-[11px] text-codialis-gray">
          En attente de la validation de la direction : ni transmission ni signature d’ici là.
        </p>
      )}
      {convention.etat === 'BROUILLON' && soumissionOuverte && (
        <p className="text-[11px] text-codialis-gray">
          Brouillon : la direction la valide avant toute transmission ou signature.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {soumissionOuverte && (
          <button
            type="button"
            disabled={enCours}
            onClick={() => soumettre.mutate({ documentId, apprenantId }, { onSuccess: confirmer })}
            className={`${CLASSE_BOUTON} border-codialis-navy bg-codialis-navy text-white hover:bg-codialis-deep`}
          >
            Soumettre à la validation
          </button>
        )}
        {convention.refusValidation === null && (
          <button
            type="button"
            disabled={enCours}
            onClick={() => valider.mutate({ documentId, apprenantId }, { onSuccess: confirmer })}
            className={`${CLASSE_BOUTON} border-transparent bg-codialis-mint/20 text-codialis-accent-texte hover:bg-codialis-mint/30`}
          >
            Valider la convention
          </button>
        )}
        {convention.refusRenvoi === null && (
          <button
            type="button"
            disabled={enCours}
            onClick={() => setRenvoiOuvert(true)}
            className={`${CLASSE_BOUTON} gap-1.5 border-codialis-navy/20 text-codialis-navy hover:bg-white`}
          >
            <CornerUpLeft className="h-3.5 w-3.5" aria-hidden />
            Renvoyer en brouillon
          </button>
        )}
      </div>

      {erreur && (
        <p role="alert" className="text-[11px] text-red-700">
          {messageErreur(erreur, 'Le geste n’a pas pu être enregistré.')}
        </p>
      )}

      {renvoiOuvert && (
        <ModaleRenvoiConvention
          documentId={documentId}
          apprenantId={apprenantId}
          intitule="la convention de formation"
          onFermer={() => setRenvoiOuvert(false)}
        />
      )}
    </div>
  );
}

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

/** Renvoi en brouillon, motif exigé : il part à qui a soumis la convention. */
export function ModaleRenvoiConvention({
  documentId,
  apprenantId,
  intitule,
  onFermer,
}: {
  documentId: string;
  apprenantId: string;
  intitule: string;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const renvoyer = useRenvoyerConvention();
  const [motif, setMotif] = useState('');

  const confirmer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    renvoyer.mutate(
      { documentId, apprenantId, motif },
      {
        onSuccess: (resultat) => {
          toast(resultat.message);
          onFermer();
        },
      },
    );
  };

  return (
    <Modale
      titre={`Renvoyer ${intitule} en brouillon`}
      sousTitre="Le motif est adressé à qui l’a soumise, dans son espace et par e-mail"
      ouverte
      onFermer={onFermer}
    >
      <form onSubmit={confirmer} className="space-y-4 p-4 sm:p-6">
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Motif du renvoi
          </span>
          <textarea
            required
            rows={4}
            maxLength={1000}
            value={motif}
            onChange={(evenement) => setMotif(evenement.target.value)}
            placeholder="Ce qui est à corriger avant une nouvelle soumission"
            className={`${CLASSE_CHAMP} resize-y`}
          />
        </label>
        <p className="text-xs text-codialis-gray">
          La convention repart en brouillon : elle se corrige depuis le dossier, se régénère au besoin, puis se soumet
          de nouveau.
        </p>
        {renvoyer.error && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageErreur(renvoyer.error, 'La convention n’a pas pu être renvoyée.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={renvoyer.isPending || motif.trim().length < MOTIF_MIN}>
            {renvoyer.isPending ? 'Envoi…' : 'Renvoyer en brouillon'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}
